import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getDefaultDateRange } from '@modules/_shared/shared/utils/dateRange';
import { computeVisibilityMetrics } from '@modules/_shared/shared/utils/statistics';
import { getTenantHostname } from '@shared/utils/tenant';

const GET_VISIBILITY_STATS = gql(/* GraphQL */ `
  query GetVisibilityStats(
    $teamId: uuid!
    $hostname: String!
    $startDate: String!
    $endDate: String!
  ) {
    impressions_data: impressions_grouped_by_column(
      agency_uuid: $teamId
      date_range_start: $startDate
      date_range_end: $endDate
      grouping_column: date
      hostname: $hostname
      order_by_column: "name"
      order_by_direction: "asc"
    ) {
      data {
        name
        count
        user_count
        average_position
      }
    }

    views_data: impressions_total(
      agency_uuid: $teamId
      date_range_start: $startDate
      date_range_end: $endDate
      hostname: $hostname
      page_types: ["agency-detail", "agent-detail"]
    ) {
      user_count
    }

    phone_clicks_data: impressions_total(
      agency_uuid: $teamId
      date_range_start: $startDate
      date_range_end: $endDate
      hostname: $hostname
      event_names: ["show_phone_number"]
    ) {
      user_count
    }

    impressions_by_locality: impressions_grouped_by_column(
      agency_uuid: $teamId
      date_range_start: $startDate
      date_range_end: $endDate
      grouping_column: page_location
      hostname: $hostname
    ) {
      data {
        count
        average_position
      }
    }
  }
`);

export const useVisibilityStats = ({ teamId }: { teamId?: string }) => {
  const { me } = useAppData();

  const hostname = me ? getTenantHostname(me.tenant_id) : null;

  const { from: startDate, to: endDate } = getDefaultDateRange();

  const {
    data: statsData,
    loading: isLoading,
    error,
  } = useQuery(GET_VISIBILITY_STATS, {
    variables: {
      teamId: teamId ?? '',
      hostname: hostname ?? '',
      startDate,
      endDate,
    },
    skip: !teamId || !hostname,
  });

  const metrics = useMemo(
    () => computeVisibilityMetrics(statsData),
    [statsData],
  );

  return { metrics, isLoading, error };
};
